<script setup lang="ts">
import { ApiError, NetworkError } from "../lib/api"
import { listIncidentShops, postIncident, postIncidentUpdate, useMessaging, type Incident } from "../composables/useMessaging"
import { dateLocale, t, tn } from "../lib/i18n"
import { SEVERITIES, severityLabel, STATUSES, statusLabel, type Severity, type Status } from "../lib/incidents"

// One incident and its updates, newest first. The Johku team also posts the next update here, sees
// its list of incidents (openIncidentId "list") and writes a new one ("new"), for every shop or the
// shops it chooses.
const emit = defineEmits<{ close: [] }>()
const hub = useMessaging()

const mode = computed(() => (hub.openIncidentId === "list" ? "list" : hub.openIncidentId === "new" ? "new" : "view"))
const incident = computed<Incident | null>(() => hub.incidents.find((i) => i.id === hub.openIncidentId) ?? null)
const team = computed(() => hub.incidentTeam)

const busy = ref(false)
const error = ref("")
/** A new incident goes out only after a second tap that says to whom */
const confirming = ref(false)

// The next update: the incident's status now, until the team changes it
const status = ref<Status>(incident.value?.status ?? "investigating")
const body = ref("")
const email = ref(true)

// A new incident
const title = ref("")
const severity = ref<Severity>("outage")
const allShops = ref(true)
const shops = ref<{ id: string; name: string }[] | null>(null)
const chosen = ref<string[]>([])
const shopQuery = ref("")
const shownShops = computed(() => {
  const q = shopQuery.value.trim().toLowerCase()
  const list = shops.value ?? []
  return q ? list.filter((s) => s.name.toLowerCase().includes(q) || s.id.includes(q)) : list
})

watch(
  allShops,
  async (all) => {
    if (all || shops.value) return
    try {
      shops.value = await listIncidentShops()
    } catch {
      shops.value = []
      error.value = t("Can't load the list of organisations right now.")
    }
  },
)

const sheetTitle = computed(() => {
  if (mode.value === "list") return t("Incidents")
  if (mode.value === "new") return t("New incident")
  return incident.value?.title ?? t("Incidents")
})

function when(iso: string): string {
  return new Date(iso).toLocaleString(dateLocale(), { day: "numeric", month: "numeric", hour: "2-digit", minute: "2-digit" })
}

function progress(p: NonNullable<Incident["updates"][number]["progress"]>): string {
  // Reached: by push or by email (without email, those without the app hear of it only in the app)
  const parts = [tn(p.recipients, "Sent to {done} of {n} person", "Sent to {done} of {n} people", { done: p.pushed + p.emailed })]
  if (p.pushed) parts.push(tn(p.pushed, "{n} by push", "{n} by push"))
  if (p.emailed) parts.push(tn(p.emailed, "{n} by email", "{n} by email"))
  return parts.join(" · ")
}

function failure(err: unknown): string {
  return err instanceof NetworkError ? t("You're offline. Try again when the connection returns.") : err instanceof ApiError ? err.message : t("Something went wrong.")
}

async function sendUpdate() {
  if (!incident.value || !body.value.trim()) return
  busy.value = true
  error.value = ""
  try {
    await postIncidentUpdate(incident.value.id, { status: status.value, body: body.value.trim(), email: email.value })
    body.value = ""
  } catch (err) {
    error.value = failure(err)
  } finally {
    busy.value = false
  }
}

async function sendNew() {
  if (!confirming.value) {
    confirming.value = true
    return
  }
  busy.value = true
  error.value = ""
  try {
    const { incidentId } = await postIncident({
      title: title.value.trim(),
      severity: severity.value,
      status: status.value,
      body: body.value.trim(),
      shops: allShops.value ? null : chosen.value,
      email: email.value,
    })
    hub.openIncidentId = incidentId
  } catch (err) {
    error.value = failure(err)
    confirming.value = false
  } finally {
    busy.value = false
  }
}

const canSendNew = computed(() => !!title.value.trim() && !!body.value.trim() && (allShops.value || chosen.value.length > 0))
watch([title, body, allShops, chosen, severity, status, email], () => (confirming.value = false), { deep: true })
</script>

<template>
  <HubSheet :title="sheetTitle" @close="emit('close')">
    <!-- The team's list -->
    <div v-if="mode === 'list'" class="body">
      <button type="button" class="hub-btn hub-btn-wide" @click="hub.openIncidentId = 'new'">
        <HubIcon name="plus" :size="20" />{{ t("New incident") }}
      </button>
      <button v-for="i in hub.incidents" :key="i.id" type="button" class="hub-sheet-row" @click="hub.openIncidentId = i.id">
        <span class="dot" :class="i.resolvedAt ? 'resolved' : i.severity" aria-hidden="true" />
        <span class="hub-sheet-row-text">
          <strong>{{ i.title }}</strong>
          <span>{{ statusLabel(t, i.status) }} · {{ when(i.updatedAt) }}</span>
        </span>
        <HubIcon name="right" :size="20" />
      </button>
      <p v-if="!hub.incidents.length" class="note">{{ t("No incidents in the last 30 days.") }}</p>
    </div>

    <!-- A new incident -->
    <form v-else-if="mode === 'new'" class="body" @submit.prevent="sendNew">
      <label>
        {{ t("What's wrong") }}
        <input v-model="title" class="hub-field" maxlength="120" required :placeholder="t('E.g. Card payments fail')" />
      </label>
      <div class="pair">
        <label>
          {{ t("Severity") }}
          <select v-model="severity" class="hub-field">
            <option v-for="s in SEVERITIES" :key="s" :value="s">{{ severityLabel(t, s) }}</option>
          </select>
        </label>
        <label>
          {{ t("Status") }}
          <select v-model="status" class="hub-field">
            <option v-for="s in STATUSES" :key="s" :value="s">{{ statusLabel(t, s) }}</option>
          </select>
        </label>
      </div>
      <fieldset class="shops">
        <legend>{{ t("For") }}</legend>
        <label class="check"><input v-model="allShops" type="radio" :value="true" /> {{ t("Every shop") }}</label>
        <label class="check"><input v-model="allShops" type="radio" :value="false" /> {{ t("Chosen shops") }}</label>
        <template v-if="!allShops">
          <input v-model="shopQuery" class="hub-field" type="search" :placeholder="t('Find a shop')" :aria-label="t('Find a shop')" />
          <div class="shop-list">
            <label v-for="s in shownShops" :key="s.id" class="check">
              <input v-model="chosen" type="checkbox" :value="s.id" /> {{ s.name }}
            </label>
          </div>
          <p class="note">{{ tn(chosen.length, "{n} shop chosen", "{n} shops chosen") }}</p>
        </template>
      </fieldset>
      <label>
        {{ t("Message to the shops") }}
        <textarea v-model="body" class="hub-field" rows="5" required maxlength="5000" :placeholder="t('What doesn\'t work, who it concerns, what to do meanwhile.')" />
      </label>
      <label class="check"><input v-model="email" type="checkbox" /> {{ t("Email those who don't have the app") }}</label>
      <p v-if="confirming" class="confirm" role="alert">
        {{
          allShops
            ? t("This goes now to the staff of every shop, by push and email. Send it?")
            : tn(chosen.length, "This goes now to the staff of {n} shop, by push and email. Send it?", "This goes now to the staff of {n} shops, by push and email. Send it?")
        }}
      </p>
      <p v-if="error" class="error" role="alert">{{ error }}</p>
      <div class="actions">
        <button class="hub-btn hub-btn-quiet" type="button" @click="hub.openIncidentId = 'list'">{{ t("Cancel") }}</button>
        <button class="hub-btn" type="submit" :disabled="busy || !canSendNew">{{ confirming ? t("Send now") : t("Send to shops") }}</button>
      </div>
    </form>

    <!-- One incident -->
    <div v-else-if="incident" class="body">
      <p class="state">
        <span class="chip" :class="incident.resolvedAt ? 'resolved' : incident.severity">
          {{ incident.resolvedAt ? statusLabel(t, "resolved") : severityLabel(t, incident.severity) }}
        </span>
        <span v-if="!incident.resolvedAt">{{ statusLabel(t, incident.status) }}</span>
      </p>
      <p v-if="team" class="note">
        {{ incident.shops ? t("For: {shops}", { shops: incident.shops.map((s) => s.name).join(", ") }) : t("For every shop") }}
      </p>

      <form v-if="team" class="update" @submit.prevent="sendUpdate">
        <label>
          {{ t("Status") }}
          <select v-model="status" class="hub-field">
            <option v-for="s in STATUSES" :key="s" :value="s">{{ statusLabel(t, s) }}</option>
          </select>
        </label>
        <label>
          {{ t("Update to the shops") }}
          <textarea v-model="body" class="hub-field" rows="3" maxlength="5000" required />
        </label>
        <label class="check"><input v-model="email" type="checkbox" /> {{ t("Email those who don't have the app") }}</label>
        <p v-if="error" class="error" role="alert">{{ error }}</p>
        <div class="actions">
          <button class="hub-btn" type="submit" :disabled="busy || !body.trim()">{{ t("Send update") }}</button>
        </div>
      </form>

      <ol class="updates">
        <li v-for="u in incident.updates" :key="u.id">
          <div class="meta">
            <strong>{{ statusLabel(t, u.status) }}</strong>
            <span>{{ when(u.createdAt) }}<template v-if="u.authorName"> · {{ u.authorName }}</template></span>
          </div>
          <p class="text">{{ u.body }}</p>
          <p v-if="u.progress" class="note">{{ progress(u.progress) }}</p>
        </li>
      </ol>
      <p v-if="!team" class="note">{{ t("The Johku team keeps you posted here until it's resolved.") }}</p>
      <button v-if="team" type="button" class="hub-btn hub-btn-quiet" @click="hub.openIncidentId = 'list'">{{ t("All incidents") }}</button>
    </div>
    <p v-else class="note body">{{ t("This incident isn't shown any more.") }}</p>
  </HubSheet>
</template>

<style scoped>
.body {
  display: grid;
  gap: 12px;
  padding: 0 8px 4px;
}

label {
  display: grid;
  gap: 6px;
  font-weight: 600;
  font-size: 0.9rem;
}

label.check {
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 400;
}

.pair {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
}

.shops {
  border: 0;
  margin: 0;
  padding: 0;
  display: grid;
  gap: 6px;
}

.shops legend {
  font-weight: 600;
  font-size: 0.9rem;
  margin-bottom: 6px;
}

.shop-list {
  max-height: 200px;
  overflow-y: auto;
  display: grid;
  gap: 4px;
  padding: 4px 2px;
}

textarea.hub-field {
  resize: vertical;
  font: inherit;
}

.actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
}

.note {
  margin: 0;
  color: var(--hub-muted);
  font-size: 0.85rem;
}

.error {
  color: var(--hub-danger);
  margin: 0;
}

.confirm {
  margin: 0;
  padding: 10px 12px;
  background: var(--hub-warning-bg);
  border-radius: var(--hub-radius);
}

.state {
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 0;
}

.chip {
  border-radius: 12px;
  padding: 3px 10px;
  font-size: 0.8rem;
  font-weight: 700;
  background: var(--hub-signal);
  color: var(--hub-on-signal);
}

.chip.outage,
.dot.outage {
  background: #b3261e;
  color: #ffffff;
}

.chip.resolved,
.dot.resolved {
  background: var(--hub-good);
  color: var(--hub-surface);
}

.dot {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  flex: none;
  background: var(--hub-signal);
  box-shadow: inset 0 0 0 1px rgb(0 0 0 / 0.2);
}

.update {
  display: grid;
  gap: 10px;
  padding: 12px;
  border-radius: var(--hub-radius);
  background: var(--hub-tint);
}

.updates {
  list-style: none;
  margin: 0;
  padding: 0;
  display: grid;
  gap: 14px;
}

.updates li {
  border-left: 3px solid var(--hub-border);
  padding-left: 12px;
  display: grid;
  gap: 4px;
}

.updates li:first-child {
  border-left-color: var(--hub-brand);
}

.meta {
  display: flex;
  flex-wrap: wrap;
  gap: 4px 10px;
  align-items: baseline;
}

.meta span {
  color: var(--hub-muted);
  font-size: 0.85rem;
}

.text {
  margin: 0;
  white-space: pre-wrap;
  overflow-wrap: anywhere;
}

.hub-theme-pos .chip,
.hub-theme-pos .update,
.hub-theme-pos .confirm {
  border-radius: 0;
}
</style>
